/**
 * What the signed-in person's organization runs (logbook decision 21), read
 * from parking-backend:
 *
 *   GET /operator/api/applications/mine?surface=&module=
 *   -> [{ id, name, surfaces, builtin, module, major, zone_ids, config }], by name
 *
 * An application runs on every surface its registry entry lists, as one setup
 * (Reservations: the dashboard's calendar and the Outlook add-in); `surface`
 * asks for those that run on it.
 *
 * Any member of the organization may ask; it names applications and grants
 * nothing. The services still decide what the account may do in each zone.
 */
import { authHeaders } from '@/services/api'
import { getToken, refreshToken } from '@/services/keycloakService'

export type Surface = 'enforcement' | 'dashboard' | 'outlook'

export interface Application {
  id: string
  name: string
  surfaces: Surface[]
  builtin: string | null
  module: string | null
  major: number | null
  zone_ids: string[]
  config: Record<string, unknown> | null
}

const OPERATOR_API = (
  import.meta.env.VITE_OPERATOR_API_URL || 'https://api.parkeze.com/operator/api'
).replace(/\/+$/, '')

export async function fetchMyApplications(filter: {
  surface: Surface
  module: string
}): Promise<Application[]> {
  const url = `${OPERATOR_API}/applications/mine?${new URLSearchParams(filter)}`
  const request = async () => fetch(url, { headers: { Accept: 'application/json', ...(await authHeaders()) } })

  let resp = await request()
  if (resp.status === 401 && getToken() && (await refreshToken())) resp = await request()
  if (!resp.ok) {
    const body = (await resp.json().catch(() => null)) as { message?: unknown; error?: unknown } | null
    const message = typeof body?.message === 'string' ? body.message : typeof body?.error === 'string' ? body.error : null
    throw new Error(message || `Applications request failed: ${resp.status}`)
  }
  const body: unknown = await resp.json()
  return Array.isArray(body) ? body.filter(isApplication) : []
}

function isApplication(value: unknown): value is Application {
  if (!value || typeof value !== 'object') return false
  const a = value as Record<string, unknown>
  return (
    typeof a.id === 'string' &&
    typeof a.name === 'string' &&
    Array.isArray(a.zone_ids) &&
    a.zone_ids.every((z) => typeof z === 'string')
  )
}
